import { useMemo } from 'react';
import useChartColors from './useChartColors';
import { useReport } from '../ReportContext';
import { getEffectiveKeyValue } from './useDynamicSeriesUtils';

/**
 * Creates enhanced series objects with color management functions
 * @param {Array} series - Series array to enhance
 * @param {Object} chartColors - Color utilities
 * @returns {Array} - Enhanced series with color functions
 */
const enhanceSeriesWithColors = (series, chartColors) => {
  return series.map((item, index) => {
    // Create a function to get color by index for this specific series
    const getColorByIndex = (idx, category) => {
      // If series has predefined colors, use those
      if (item.colors && Array.isArray(item.colors) && item.colors.length > 0) {
        return item.colors[idx % item.colors.length];
      }
      
      // If series has a specific color defined
      if (item.color) {
        return item.color;
      }
      
      // Otherwise use the category from series or default
      const colorCategory = category || item.colorCategory || 'primary';
      return chartColors.getColorByIndex(idx, colorCategory);
    };
    
    // Create a function to generate multiple colors for this series
    const generateColors = (count, category) => {
      // If series has predefined colors, use those (with repetition if needed)
      if (item.colors && Array.isArray(item.colors) && item.colors.length > 0) {
        return Array.from({ length: count }, (_, i) => 
          item.colors[i % item.colors.length]
        );
      }
      
      // Otherwise use the category from series or default
      const colorCategory = category || item.colorCategory || 'primary';
      return chartColors.generateColors(count, colorCategory);
    };
    
    // Create a function to get all colors for this series
    const getAllColors = (category) => {
      // If series has predefined colors, return those
      if (item.colors && Array.isArray(item.colors) && item.colors.length > 0) {
        return [...item.colors];
      }
      
      // Otherwise return all colors from the specified category
      const colorCategory = category || item.colorCategory || 'primary';
      return chartColors.getColorsByCategory(colorCategory);
    };
    
    // Return enhanced series object with color functions
    return {
      ...item,
      // Add index for reference
      index,
      // Add color management functions
      getColorByIndex,
      generateColors,
      getAllColors,
      // Add a default color for convenience
      defaultColor: item.color || getColorByIndex(0, item.colorCategory)
    };
  });
};

/**
 * Generates dynamic series based on provided data
 * @param {Array} seriesTemplates - Original series templates
 * @param {Array} data - Data array to extract unique keys from
 * @returns {Array} - Array of dynamic series
 */
const generateDynamicSeries = (seriesTemplates, data) => {
  if (!data || !data.length || !seriesTemplates || !seriesTemplates.length) {
    return seriesTemplates;
  }

  let processedSeries = [];
    
  seriesTemplates.forEach((seriesTemplate) => {
    if (seriesTemplate.dynamicSeries) {
      const { keyField, colors } = seriesTemplate.dynamicSeries;
      
      // Find unique values for the keyField in the data, with fallback support
      const uniqueKeys = [...new Set(data.map(item => getEffectiveKeyValue(item, keyField)))].filter(Boolean);
      
      // Create a series for each unique value
      uniqueKeys.forEach((keyValue, keyIndex) => {
        processedSeries.push({
          ...seriesTemplate,
          // Create a new key that combines the original key and the keyField value
          key: `${seriesTemplate.key}_${keyValue}`,
          // Set the label to the keyField value
          label: keyValue,
          // Set the original template key for reference
          templateKey: seriesTemplate.key,
          // Set the keyField and value for filtering
          keyField,
          keyValue,
          // Assign a color from the colors array
          color: colors?.[keyIndex % colors.length] || seriesTemplate.color,
          // Store the original seriesTemplate for reference
          _original: seriesTemplate
        });
      });
    } else {
      // If not a dynamic series, just add it as is
      processedSeries.push(seriesTemplate);
    }
  });
  
  return processedSeries;
};

/**
 * Custom hook for managing series configuration with enhanced color management
 * 
 * @param {Object} chartConfig - The chart configuration object containing series (array or function)
 * @returns {Object} Object containing enhanced series array with color management functions
 */
const useSeries = (chartConfig) => {
  const chartColors = useChartColors();
  const { data: reportData } = useReport();
  
  // Extract series templates from config
  const getSeriesTemplates = () => {
    let templates = [];
    
    if (typeof chartConfig?.series === 'function') {
      try {
        const result = chartConfig.series();
        templates = Array.isArray(result) ? result : [];
      } catch (error) {
        console.error('Error calling series function:', error);
        templates = [];
      }
    } else if (Array.isArray(chartConfig?.series)) {
      templates = chartConfig.series;
    }
    
    return templates;
  };
  
  const enhancedSeries = useMemo(() => {
    // Get series templates
    const seriesTemplates = getSeriesTemplates();
    if (!seriesTemplates.length) return [];
    
    // Process dynamic series if needed
    let processedSeries = seriesTemplates;
    const hasDynamicConfig = seriesTemplates.some(s => s.dynamicSeries);
    
    // Only process dynamic series if we have report data
    if (reportData?.length > 0 && hasDynamicConfig) {
      processedSeries = generateDynamicSeries(seriesTemplates, reportData);
    }
    
    // Enhance series with color functions
    return enhanceSeriesWithColors(processedSeries, chartColors);
  }, [chartConfig, chartColors, reportData]);
  
  // Return an object with the series property for easier destructuring
  return {
    series: enhancedSeries,
    // Add utility functions that work on all series if needed
    getSeriesById: (id) => enhancedSeries.find(s => s.id === id || s.key === id),
    getSeriesByIndex: (index) => enhancedSeries[index],
    showLegend: () => enhancedSeries.some(series => series.showLegend !== false),
    // Function to generate dynamic series with provided data
    generateDynamicSeriesWithData: (data) => {
      const seriesTemplates = getSeriesTemplates();
      const dynamicSeriesTemplates = generateDynamicSeries(seriesTemplates, data);
      return enhanceSeriesWithColors(dynamicSeriesTemplates, chartColors);
    },
    isLoading: false
  };
};

export default useSeries; 